import type { AccountBranding } from '@deskworks/shared-types';
import { useEffect, useSyncExternalStore } from 'react';

/**
 * An account's own look (docs/features/branding.md): from its main color and sidebar color, the theme's tokens for light
 * and dark (src/index.css), so buttons stay readable and the dark theme stays dark whatever the brand's colors are.
 */

interface Oklch {
  l: number;
  c: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('')}`;
}

/** sRGB (0–255) to OKLCH (Björn Ottosson's OKLab). */
function rgbToOklch([r8, g8, b8]: [number, number, number]): Oklch {
  const [r, g, b] = [r8, g8, b8].map((v) => toLinear(v / 255));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

/** Linear sRGB of an OKLCH color, unclamped. */
function oklchToLinear({ l: L, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** Relative luminance (WCAG) of an OKLCH color. */
function luminance(color: Oklch) {
  const [r, g, b] = oklchToLinear(color).map((v) => clamp(v, 0, 1));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const css = ({ l, c, h }: Oklch) => `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;

/** Text on a background of this color: near white or near black, whichever reads better. */
function onColor(bg: Oklch): Oklch {
  const light = { l: 0.985, c: Math.min(bg.c * 0.1, 0.01), h: bg.h };
  const dark = { l: 0.18, c: Math.min(bg.c * 0.3, 0.03), h: bg.h };
  const y = luminance(bg);
  return contrast(y, luminance(light)) >= contrast(y, luminance(dark)) ? light : dark;
}

/** The main color in one theme: as the brand has it where it reads on that theme's background, else lighter or darker. */
function primaryFor(brand: Oklch, dark: boolean): Oklch {
  if (!dark) return { l: Math.min(brand.l, 0.72), c: brand.c, h: brand.h };
  // A black or gray brand turns near white in the dark theme rather than a dull gray.
  return { l: brand.c < 0.03 ? 0.88 : clamp(brand.l, 0.62, 0.82), c: Math.min(brand.c, 0.2), h: brand.h };
}

/** Tokens inside the sidebar on its own background: its text, hover, borders, and a main color that stands out on it. */
function sidebarTokens(bg: Oklch, primary: Oklch) {
  const darkBg = luminance(bg) < 0.18;
  const step = darkBg ? 1 : -1;
  const tint = (dl: number) => css({ l: clamp(bg.l + step * dl, 0, 1), c: bg.c, h: bg.h });
  const fg = darkBg ? { l: 0.97, c: Math.min(bg.c * 0.1, 0.01), h: bg.h } : { l: 0.22, c: Math.min(bg.c * 0.2, 0.02), h: bg.h };
  const muted = darkBg ? { l: 0.8, c: Math.min(bg.c * 0.25, 0.03), h: bg.h } : { l: 0.47, c: Math.min(bg.c * 0.3, 0.04), h: bg.h };
  const own = contrast(luminance(primary), luminance(bg)) >= 3 ? primary : { ...primary, l: darkBg ? 0.85 : 0.42 };
  return {
    '--sidebar': css(bg),
    '--sidebar-foreground': css(fg),
    '--sidebar-accent': tint(0.07),
    '--sidebar-border': tint(0.09),
    '--foreground': css(fg),
    '--muted-foreground': css(muted),
    '--muted': tint(0.06),
    '--primary': css(own),
    '--primary-foreground': css(onColor(own)),
    '--ring': css(own),
  };
}

const block = (selector: string, tokens: Record<string, string>) =>
  `${selector}{${Object.entries(tokens)
    .map(([k, v]) => `${k}:${v}`)
    .join(';')}}`;

/**
 * The CSS that puts an account's colors over the theme's (empty: the Deskworks look). The sidebar's tokens apply inside
 * `.brand-sidebar` (the app's sidebar and the preview of it).
 */
export function brandCss(branding: Pick<AccountBranding, 'primaryColor' | 'sidebarColor'> | null): string {
  if (!branding?.primaryColor && !branding?.sidebarColor) return '';
  const out: string[] = [];
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};
  // Deskworks' indigo when only the sidebar is the brand's.
  const brand = branding.primaryColor ? rgbToOklch(hexToRgb(branding.primaryColor)) : { l: 0.51, c: 0.2, h: 274 };
  const pLight = primaryFor(brand, false);
  const pDark = primaryFor(brand, true);
  if (branding.primaryColor) {
    const { c, h } = brand;
    Object.assign(light, {
      '--primary': css(pLight),
      '--primary-foreground': css(onColor(pLight)),
      '--ring': css({ ...pLight, l: clamp(pLight.l + 0.08, 0, 0.75) }),
      '--accent': css({ l: 0.955, c: Math.min(c * 0.3, 0.03), h }),
      '--accent-foreground': css({ l: 0.32, c: Math.min(c, 0.12), h }),
    });
    Object.assign(dark, {
      '--primary': css(pDark),
      '--primary-foreground': css(onColor(pDark)),
      '--ring': css({ ...pDark, l: pDark.l - 0.05 }),
      '--accent': css({ l: 0.28, c: Math.min(c * 0.4, 0.05), h }),
      '--accent-foreground': css({ l: 0.92, c: Math.min(c * 0.3, 0.05), h }),
    });
    if (!branding.sidebarColor) {
      // The neutral sidebar, a breath of the brand's hue.
      Object.assign(light, { '--sidebar': css({ l: 0.975, c: Math.min(c * 0.06, 0.008), h }), '--sidebar-accent': css({ l: 0.93, c: Math.min(c * 0.12, 0.016), h }) });
      Object.assign(dark, { '--sidebar': css({ l: 0.155, c: Math.min(c * 0.06, 0.01), h }), '--sidebar-accent': css({ l: 0.24, c: Math.min(c * 0.12, 0.02), h }) });
    }
  }
  out.push(block(':root:not(.dark)', light), block('.dark', dark));
  if (branding.sidebarColor) {
    const bg = rgbToOklch(hexToRgb(branding.sidebarColor));
    // In the dark theme a light sidebar would glare: as dark as the theme's, the brand's hue kept.
    const bgDark = { l: Math.min(bg.l, 0.24), c: Math.min(bg.c, 0.08), h: bg.h };
    out.push(block(':root:not(.dark) .brand-sidebar', sidebarTokens(bg, pLight)), block('.dark .brand-sidebar', sidebarTokens(bgDark, pDark)));
  }
  return out.join('\n');
}

// ---------------------------------------------------------------- colors of a logo

/**
 * The colors a logo is made of, most telling first: what covers much of it and is colorful, near-identical shades
 * merged, plain white and transparent left out. At most `max`.
 */
export function logoColors(data: ImageData, max = 6): string[] {
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  const step = Math.max(1, Math.floor((data.width * data.height) / 20_000));
  for (let i = 0; i < data.width * data.height; i += step) {
    const o = i * 4;
    if (data.data[o + 3] < 160) continue;
    const [r, g, b] = [data.data[o], data.data[o + 1], data.data[o + 2]];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    bucket.n++;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    buckets.set(key, bucket);
  }
  const total = [...buckets.values()].reduce((sum, b) => sum + b.n, 0);
  const candidates = [...buckets.values()]
    .filter((b) => b.n / total > 0.004)
    .map((b) => {
      const rgb: [number, number, number] = [b.r / b.n, b.g / b.n, b.b / b.n];
      const lch = rgbToOklch(rgb);
      return { rgb, lch, score: (b.n / total) * (0.25 + Math.min(lch.c, 0.25) * 6) };
    })
    .filter((x) => !(x.lch.l > 0.94 && x.lch.c < 0.03))
    .sort((a, b) => b.score - a.score);
  const picked: typeof candidates = [];
  for (const x of candidates) {
    if (picked.some((p) => distance(p.lch, x.lch) < 0.09)) continue;
    picked.push(x);
    if (picked.length === max) break;
  }
  return picked.map((p) => rgbToHex(...p.rgb));
}

/** Distance in OKLab, about 0.02 for a just noticeable difference. */
function distance(a: Oklch, b: Oklch) {
  const ax = a.c * Math.cos((a.h * Math.PI) / 180);
  const ay = a.c * Math.sin((a.h * Math.PI) / 180);
  const bx = b.c * Math.cos((b.h * Math.PI) / 180);
  const by = b.c * Math.sin((b.h * Math.PI) / 180);
  return Math.hypot(a.l - b.l, ax - bx, ay - by);
}

/** Of a logo's colors, the one for buttons and highlights: the first colorful one (a gray logo: its first color). */
export function suggestedPrimary(colors: string[]): string | undefined {
  return colors.find((hex) => rgbToOklch(hexToRgb(hex)).c >= 0.05) ?? colors[0];
}

// ---------------------------------------------------------------- applying it

type Look = Pick<AccountBranding, 'primaryColor' | 'sidebarColor' | 'logoUrl'>;

/** A look shown before it is saved (the branding dialog), over the account's. */
let preview: Look | null = null;
const listeners = new Set<() => void>();

export function setBrandPreview(next: Look | null) {
  preview = next;
  listeners.forEach((l) => l());
}

function usePreview() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => preview,
  );
}

/** The look in effect for an account: the one being previewed, else its own. */
export function useLook(branding: AccountBranding | null | undefined): Look | null {
  return usePreview() ?? branding ?? null;
}

const DEFAULT_ICON = '/favicon.svg';

/**
 * Puts the account's look (or the one being previewed) on the app: its colors as a style sheet after the theme's, its logo
 * as the tab's icon. Returns the look in effect, for the logo in the sidebar.
 */
export function useBrandLook(branding: AccountBranding | null | undefined): Look | null {
  const shown = useLook(branding);
  const sheet = brandCss(shown);
  useEffect(() => {
    let el = document.getElementById('deskworks-brand');
    if (!sheet) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement('style');
      el.id = 'deskworks-brand';
      document.head.appendChild(el);
    }
    el.textContent = sheet;
  }, [sheet]);
  const icon = shown?.logoUrl ?? null;
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    link.type = icon ? '' : 'image/svg+xml';
    link.href = icon ?? DEFAULT_ICON;
  }, [icon]);
  return shown;
}
